import { useState, useEffect, useCallback } from 'react';
import { usePolling } from '@/shared/hooks/usePolling';
import { messagesService } from '../services/messagesService';

interface UseUnreadCountOptions {
  /**
   * Enable/disable polling
   * @default true
   */
  enabled?: boolean;
  /**
   * Callback when max retries exceeded
   */
  onError?: () => void;
}

export function useUnreadCount({ enabled = true, onError }: UseUnreadCountOptions = {}) {
  const [count, setCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await messagesService.getUnreadCount();
      setCount(response.count);
      setHasError(false);
      setIsLoading(false);
    } catch (error) {
      console.error('[useUnreadCount] Error fetching unread count:', error);
      setHasError(true);
      throw error; // Re-throw to trigger polling backoff
    }
  }, []);

  const handleMaxRetriesExceeded = useCallback(() => {
    setHasError(true);
    onError?.();
  }, [onError]);

  const { start, stop } = usePolling({
    callback: fetchUnreadCount,
    interval: 15000, // 15 seconds
    pauseOnHidden: true,
    enableBackoff: true,
    maxRetries: 3,
    onMaxRetriesExceeded: handleMaxRetriesExceeded,
  });

  useEffect(() => {
    if (enabled) {
      start();
    } else {
      stop();
    }

    return () => {
      stop();
    };
  }, [enabled, start, stop]);

  const refetch = useCallback(async () => {
    setIsLoading(true);
    try {
      await fetchUnreadCount();
    } catch {
      // Error already logged in fetchUnreadCount
    }
  }, [fetchUnreadCount]);

  return {
    count,
    isLoading,
    hasError,
    refetch,
  };
}
